import { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import { ROUTES } from "./router";
import { TAccount, TAccountData } from "./redux/reducers/account";
import { localStorageConstants } from "./utils/constants";
import apiService from "./services/api";
import {
  getQuerystringParams,
  handleGetDataFromLocalStorage,
  handleSetDataToLocalStorage,
} from "./utils/miscellaneous";
import Modal from "./components/ui-v2/Modal";
import { useTypedSelector } from "./hooks/useTypedSelector";
import { useTypedDispatch } from "./hooks/useTypedDispatch";
import { setLogoutModalState } from "./redux/reducers/globalState";
import Header from "./components/Header";
import { useSelector } from "react-redux";
import { RootState } from "./redux/store";
import axios, { CancelTokenSource } from "axios";
import { fetchAccounts } from "./redux/actions";
import { useNavigationManager } from "./hooks/useNavigationManager";
import Sidebar from "./components/ui-v2/Sidebar";

type TAppProps = {
  component: JSX.Element;
  style?: React.CSSProperties;
};

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const Container = styled.div<{ $shouldDecreaseHeight?: boolean }>`
  flex: 1 1 auto;
  height: ${({ $shouldDecreaseHeight }) =>
    $shouldDecreaseHeight ? "calc(100% - 185px)" : "100%"};
  overflow: auto;
  @media (max-width: 960px) {
    display: none;
  }
`;

const App: React.FC<TAppProps> = ({
  component,
  style = { marginTop: "72px" },
}) => {
  const isSidebarFixed = useSelector(
    (state: RootState) => state.sidebar.isFixed
  );
  const { goTo, setQuerystring } = useNavigationManager();

  const dispatch = useTypedDispatch();

  const reduxQuerystring = getQuerystringParams();

  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  const logoutModal = useTypedSelector(
    (state) => state.globalState.data.logoutModal
  );

  const accountsData = useTypedSelector((state) => state.account.data);

  const fetchData = useCallback(async () => {
    try {
      cancelTokenSource.current = axios.CancelToken.source();

      const queryStringParams = getQuerystringParams();

      setQuerystring(queryStringParams);

      const localUserData = localStorage.getItem(
        localStorageConstants.USER_DATA
      );
      if (!localUserData) {
        apiService.getUserData(cancelTokenSource.current.token);
      }

      let localAccountCount = accountsData.count;
      let accounts;

      if (isNaN(localAccountCount) || localAccountCount < 0) {
        let accountsResponse;

        if (!queryStringParams.search) {
          accountsResponse = await dispatch(
            fetchAccounts({ cancelToken: cancelTokenSource.current.token })
          ).unwrap();
        } else {
          accountsResponse = await apiService.getAccounts(
            cancelTokenSource.current.token
          );
        }

        accounts = accountsResponse as TAccountData;

        if (accounts.count) {
          // console.log('setting accounts count: ', accounts.count)
          handleSetDataToLocalStorage(
            localStorageConstants.ACCOUNTS_COUNT,
            accounts.count
          );
        }

        localAccountCount = accounts.count;
      }

      const localSelectAccount = handleGetDataFromLocalStorage(
        localStorageConstants.SELECT_ACCOUNT
      ) as TAccount;

      if (
        (!localSelectAccount && localAccountCount > 1) ||
        localAccountCount === 0
      ) {
        goTo(ROUTES.ACCOUNTS, reduxQuerystring);
      } /* else if (localSelectAccount && !localOrganizations) {
         await apiService.getOrganizations(localSelectAccount.uuid);
      } */ else if (
        [ROUTES.ACCOUNTS, ROUTES.LOGIN, ROUTES.HOME].includes(
          window.location.pathname
        )
      ) {
        if (
          [ROUTES.ACCOUNTS].includes(window.location.pathname) &&
          window.location.search.length > 0
        ) {
          // goTo(ROUTES.ACCOUNTS, reduxQuerystring);
        } else goTo(ROUTES.HOME, reduxQuerystring);
      }
    } catch (e) {}
  }, [accountsData.count]);

  useEffect(() => {}, [logoutModal.shouldShow]);

  useEffect(() => {
    const authorization = localStorage.getItem("Authorization");
    const isAllowed = authorization && authorization.length > 0;

    if (!isAllowed) {
      goTo(ROUTES.LOGIN);
    } else {
      fetchData();
    }

    return () => {
      if (cancelTokenSource.current) cancelTokenSource.current.cancel();
      Object.values(localStorageConstants).forEach((value) => {
        if (
          !Array.isArray(value) &&
          !localStorageConstants.CACHE_CLEAR_WHITE_LIST.includes(value)
        ) {
          localStorage.removeItem(value);
        }
      });
    };
  }, []);

  const hasSidebar =
    window.location.pathname.includes(ROUTES.INVOICES) ||
    window.location.pathname.includes(ROUTES.INVOICES_ISSUED) ||
    window.location.pathname.includes(ROUTES.PAYMENT_SLIP) ||
    window.location.pathname.includes(ROUTES.SETTINGS);

  // Calculate sidebar style for Home content
  const sidebarStyle =
    window.location.pathname.includes(ROUTES.INVOICES) ||
    window.location.pathname.includes(ROUTES.INVOICES_ISSUED) ||
    window.location.pathname.includes(ROUTES.PAYMENT_SLIP) ||
    window.location.pathname.includes(ROUTES.SETTINGS)
      ? {
          marginLeft: (isSidebarFixed ? 240 : 48) + 24,
          transition: "margin-left 0.2s cubic-bezier(0.4,0,0.2,1)",
        }
      : {};

  return (
    <AppWrapper>
      {!window.location.pathname.includes(ROUTES.ACCOUNTS) && <Header />}
      {hasSidebar && <Sidebar />}
      <Container
        $shouldDecreaseHeight={!window.location.href.includes(ROUTES.ACCOUNTS)}
        style={style}
      >
        <div
          style={{
            margin: `0 24px`,
            ...sidebarStyle,
          }}
        >
          {component}
        </div>
        <Modal
          shouldOpen={logoutModal.shouldShow}
          onClose={() => {
            dispatch(setLogoutModalState({ shouldShow: false }));
          }}
          title="Sair da sua conta"
          icon={{ type: "exclamationTriangle", color: "orange" }}
          main={{
            style: "textOnly",
            content: [
              {
                style: "default",
                text: "Tem certeza que deseja sair?",
                shouldBreakLine: true,
              },
            ],
          }}
          footer={{
            style: "default",
            buttons: [
              {
                icon: "noIcon",
                text: "Cancelar",
                variant: "secondary",
                onClick() {
                  dispatch(setLogoutModalState({ shouldShow: false }));
                },
              },
              {
                iconPosition: "left",
                icon: "logout",
                text: "Sim, sair",
                variant: "orange",
                onClick() {
                  localStorage.clear();
                  dispatch(setLogoutModalState({ shouldShow: false }));
                  // Clear the URL (remove querystring/hash) and go to login
                  window.location.href = ROUTES.LOGIN;
                },
              },
            ],
          }}
        />
      </Container>
    </AppWrapper>
  );
};

export default App;
